import { useEffect, useMemo, useRef, useState } from "react";
import moment, { Moment } from "moment";
import classNames from "classnames";
import { useIntl } from "react-intl";
import { to24HourTime } from "@/util/dateUtil";
import { warningToast } from "@/component/Toast";

import { getFormattedDate } from "../helper";


interface DateInputProps {
    value?: Moment;
    useTime: boolean;
    useFuture: boolean;
    "aria-label"?: string;
    onInputValidDate: (date: Moment) => void;
}

const DateInput: React.FC<DateInputProps> = (props) => {
    const { value, useTime, useFuture, "aria-label": ariaLabel, onInputValidDate } = props;

    const intl = useIntl();

    const inputRef = useRef<HTMLInputElement>(null);
    const [inputText, setInputText] = useState<string>(getFormattedDate(value, useTime) ?? "");
    const today = useMemo(() => moment().startOf('day'), []);

    const getDateFromInput = (input: string) => {
        if (!input) {
            return;
        }
        const [date, time, meridiem] = input.split(' ');

        const dateFromInput = moment(date, 'YYYY.MM.DD', true);
        if (!dateFromInput?.isValid()) {
            return;
        }

        if (!useTime) {
            dateFromInput.startOf('day');
            if (value) {
                dateFromInput.set('hour', value.hour()).set('minute', value.minute());
            }
            return dateFromInput;
        } else {
            const [hour, minute] = time.split(':').map(num => parseInt(num));
            const hour24 = to24HourTime(hour, meridiem as "AM" | "PM");
            dateFromInput.set('hour', hour24).set('minute', minute);
    
            const enMoment = dateFromInput.clone().locale('en');
            if (enMoment.format("YYYY.MM.DD hh:mm A") !== input
                && enMoment.format("YYYY.MM.DD h:mm A") !== input) {
                return;
            }
            return dateFromInput;
        }
    };

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const targetValue = e.target.value;
        setInputText(targetValue);

        const date = getDateFromInput(targetValue);
        if (!date) {
            return;
        }
        if (!useFuture && date.isAfter(today, 'day')) {
            warningToast(intl.formatMessage({ id: "i00029" }));
            return;
        }
        onInputValidDate(date);
    };

    const onBlur = () => {
        setInputText(getFormattedDate(value, useTime));
        if (!getDateFromInput(inputText)) {
            warningToast(intl.formatMessage({ id: useTime ? "i00030" : "i00030_1" }));
        };
    };

    useEffect(() => {
        if (inputRef.current && document.activeElement === inputRef.current) {
            return;
        }
        setInputText(getFormattedDate(value, useTime));
    }, [value, useTime]);

    return (
        <input ref={inputRef}
               aria-label={ariaLabel}
               className={classNames(
                   "input-default py-1.5 px-2 outline-chat42 text-[13px] text-chat42",
                   useTime ? "w-[130px]" : "w-20"
               )}
               value={inputText}
               placeholder={useTime ? "yyyy.mm.dd hh:mm AM/PM" : "yyyy.mm.dd"}
               onChange={onChange}
               onBlur={onBlur}
        />
    );
};

export default DateInput;

